import React, { useState } from 'react';
import { Smartphone, Loader } from 'lucide-react';
import { initiatePhonePePayment } from '../lib/phonepe';
import { supabase } from '../lib/supabase';

interface PhonePeButtonProps {
  amount: number;
  orderId: string;
  onSuccess: () => void;
  onError: (error: string) => void;
}

const PhonePeButton: React.FC<PhonePeButtonProps> = ({
  amount,
  orderId,
  onSuccess,
  onError
}) => {
  const [loading, setLoading] = useState(false);

  const handlePayment = async () => {
    try {
      setLoading(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get user profile for contact details
      const { data: profile } = await supabase
        .from('profiles')
        .select('phone')
        .eq('id', user.id)
        .single();

      if (!profile?.phone) {
        throw new Error('Please update your phone number in profile');
      }

      const callbackUrl = `${window.location.origin}/checkout/verify`;
      
      const result = await initiatePhonePePayment({
        amount,
        orderId,
        customerEmail: user.email || '',
        customerPhone: profile.phone,
        callbackUrl
      });

      if (result.success) {
        // Update order with payment initiation
        await supabase
          .from('orders')
          .update({
            payment_method: 'phonepe',
            payment_status: 'pending',
            payment_gateway: 'phonepe'
          })
          .eq('id', orderId);

        // Redirect to PhonePe payment page
        window.location.href = result.redirectUrl;
      } else {
        throw new Error('Failed to initiate payment');
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handlePayment}
      disabled={loading}
      className="w-full bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
    >
      {loading ? (
        <>
          <Loader className="animate-spin h-5 w-5 mr-2" />
          Processing...
        </>
      ) : (
        <>
          <Smartphone className="h-5 w-5 mr-2" />
          Pay with PhonePe
        </>
      )}
    </button>
  );
};

export default PhonePeButton;